import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { applyTrackedUpdate } from "../services/jobTracker";
import { JOB_STATUS } from "../constants/jobProgress";

const JobProgressContext = createContext();

export const JobProgressProvider = ({ children }) => {
  const [activeJobs, setActiveJobs] = useState([]);
  // Set of plan IDs whose copy jobs have just completed. Plan Details views consume and clear their own entry.
  const [completedJobPlanIds, setCompletedJobPlanIds] = useState(new Set());

  // removeJob is defined first because updateJob depends on it (useCallback dep array).
  const removeJob = useCallback((jobId) => {
    setActiveJobs((prev) => (prev.some((job) => job.jobId === jobId) ? prev.filter((job) => job.jobId !== jobId) : prev));
  }, []);

  // options.tracked: COA jobs (trackCoaJob); plan jobs keep the original shape
  const addJob = useCallback((jobId, planName, planIds = [], options = {}) => {
    const ids = Array.isArray(planIds) ? planIds : (planIds ? [planIds] : []);
    const job = options.tracked
      ? { jobId, planName, planIds: ids, progress: 0, status: JOB_STATUS.IN_PROGRESS, message: planName, tracked: true }
      : { jobId, planName, planIds: ids, progress: 0, status: `Job "${planName}" started` };
    setActiveJobs((prev) => [...prev.filter((j) => j.jobId !== jobId), job]);
  }, []);

  const updateJob = useCallback((jobId, data, planIds = null) => {
    const isCompleted =
      data.status === "COMPLETED" || parseInt(data.completedPercentage, 10) === 100;
    const isFailed = data.status === "FAILED";
    setActiveJobs((prev) => {
      if (isCompleted) {
        const resolvedIds = planIds
          ? (Array.isArray(planIds) ? planIds : [planIds])
          : (prev.find((j) => j.jobId === jobId)?.planIds ?? []);
        if (resolvedIds.length > 0) {
          setCompletedJobPlanIds((prevSet) => {
            const next = new Set(prevSet);
            resolvedIds.forEach((id) => next.add(String(id)));
            return next;
          });
        }
      }
      const existing = prev.find((j) => j.jobId === jobId);
      if (!existing) return prev;
      if (existing.tracked) {
        return applyTrackedUpdate(prev, jobId, data);
      }
      return prev.map((job) =>
        job.jobId === jobId
          ? {
              ...job,
              progress: parseInt(data.completedPercentage, 10),
              status: data.status,
              message: data.message,
            }
          : job
      );
    });
    // Auto-prune finished jobs so cleanup doesn't depend on the toast being mounted
    // (a copy job that finishes while the user is away from the plan has no visible toast).
    // Delays are slightly longer than the toast's own removal so the on-screen toast UX is unaffected.
    if (isCompleted) {
      setTimeout(() => removeJob(jobId), 1500);
    } else if (isFailed) {
      setTimeout(() => removeJob(jobId), 3500);
    }
  }, [removeJob]);

  const clearCompletedJobPlanId = useCallback((planId) => {
    setCompletedJobPlanIds((prev) => {
      const next = new Set(prev);
      next.delete(String(planId));
      return next;
    });
  }, []);

  const restoreJob = useCallback((jobId, planName, progress, status, message, planIds = []) => {
    const ids = Array.isArray(planIds) ? planIds : (planIds ? [planIds] : []);
    setActiveJobs((prev) => [
      ...prev.filter((job) => job.jobId !== jobId),
      { jobId, planName, planIds: ids, progress, status, message },
    ]);
  }, []);

  const value = useMemo(
    () => ({ activeJobs, addJob, updateJob, removeJob, restoreJob, completedJobPlanIds, clearCompletedJobPlanId }),
    [activeJobs, addJob, updateJob, removeJob, restoreJob, completedJobPlanIds, clearCompletedJobPlanId]
  );

  return (
    <JobProgressContext.Provider value={value}>
      {children}
    </JobProgressContext.Provider>
  );
};

export const useJobProgress = () => useContext(JobProgressContext);
